import React, { useState } from 'react';
import { FileText, DollarSign, Users, AlertTriangle, X, TrendingUp, Calendar, Clock } from 'lucide-react';
import { formatCurrency } from '../../utils/timeUtils';
import { LTSSService, Alert, FinancialSummary, StaffMetrics, ClientUtilization } from '../../types/ltss';

export type KPIType = 'services' | 'revenue' | 'staff' | 'alerts';

interface KPICardsProps {
  totalServices: number;
  totalRevenue: number;
  activeStaff: number;
  activeAlerts: number;
  // Detailed data for drill-down
  services?: LTSSService[];
  alerts?: Alert[];
  financials?: FinancialSummary;
  staffMetrics?: StaffMetrics[];
  clientUtilization?: ClientUtilization[];
}

export function KPICards({ 
  totalServices, 
  totalRevenue, 
  activeStaff, 
  activeAlerts,
  services = [],
  alerts = [],
  financials,
  staffMetrics = [],
  clientUtilization = [],
}: KPICardsProps) {
  const [activeCard, setActiveCard] = useState<KPIType | null>(null);

  const cards: {
    id: KPIType;
    title: string;
    value: string;
    icon: React.ElementType;
    color: string;
    bgColor: string;
    iconColor: string;
    glowColor: string;
  }[] = [
    {
      id: 'services',
      title: 'Total Services',
      value: totalServices.toLocaleString(),
      icon: FileText,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-500/10',
      iconColor: '#3b82f6',
      glowColor: 'hover:shadow-blue-500/20',
    },
    {
      id: 'revenue',
      title: 'Revenue',
      value: formatCurrency(totalRevenue),
      icon: DollarSign,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-500/10',
      iconColor: '#22c55e',
      glowColor: 'hover:shadow-green-500/20',
    },
    {
      id: 'staff',
      title: 'Active Staff',
      value: activeStaff.toString(),
      icon: Users,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-500/10',
      iconColor: '#a855f7',
      glowColor: 'hover:shadow-purple-500/20',
    },
    {
      id: 'alerts',
      title: 'Active Alerts',
      value: activeAlerts.toString(),
      icon: AlertTriangle,
      color: activeAlerts > 0 ? 'from-red-500 to-red-600' : 'from-slate-500 to-slate-600',
      bgColor: activeAlerts > 0 ? 'bg-red-500/10' : 'bg-slate-500/10',
      iconColor: activeAlerts > 0 ? '#ef4444' : '#64748b',
      glowColor: activeAlerts > 0 ? 'hover:shadow-red-500/20' : 'hover:shadow-slate-500/20',
    },
  ];

  const handleCardClick = (cardId: KPIType) => {
    setActiveCard(activeCard === cardId ? null : cardId);
  };

  // Helper to get day name from date
  const getDayName = (date: Date): string => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[date.getDay()];
  };

  // Calculate detailed breakdowns
  const getServiceBreakdown = () => {
    const byDay: Record<string, number> = {};
    const byStaff: Record<string, number> = {};
    const byClient: Record<string, number> = {};
    
    services.forEach(s => {
      // Try to get day from dayOfService, or derive from serviceDate
      let day = s.dayOfService;
      if (!day && s.serviceDate) {
        day = getDayName(new Date(s.serviceDate));
      }
      if (!day) day = 'No Date';
      
      byDay[day] = (byDay[day] || 0) + 1;
      if (s.staffName) byStaff[s.staffName] = (byStaff[s.staffName] || 0) + 1;
      if (s.clientName) byClient[s.clientName] = (byClient[s.clientName] || 0) + 1;
    });

    // Ensure all days of the week are present, in order
    const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const orderedByDay: [string, number][] = dayOrder.map(day => [day, byDay[day] || 0]);
    
    // Add "No Date" at the end if there are any
    if (byDay['No Date']) {
      orderedByDay.push(['No Date', byDay['No Date']]);
    }

    return {
      byDay: orderedByDay,
      byStaff: Object.entries(byStaff).sort((a, b) => b[1] - a[1]).slice(0, 5),
      byClient: Object.entries(byClient).sort((a, b) => b[1] - a[1]).slice(0, 5),
      totalHours: services.reduce((sum, s) => sum + (s.serviceLength || 0), 0),
    };
  };

  const getRevenueBreakdown = () => {
    const byWeek: Record<string, number> = {};
    const byStaff: Record<string, number> = {};
    
    services.forEach(s => {
      if (s.amountPaid) {
        const week = s.week || 'Unknown';
        byWeek[week] = (byWeek[week] || 0) + s.amountPaid;
        if (s.staffName) byStaff[s.staffName] = (byStaff[s.staffName] || 0) + s.amountPaid;
      }
    });

    return {
      byWeek: Object.entries(byWeek).sort((a, b) => b[1] - a[1]).slice(0, 5),
      topEarners: Object.entries(byStaff).sort((a, b) => b[1] - a[1]).slice(0, 5),
      avgPerService: services.length > 0 ? totalRevenue / services.length : 0,
    };
  };

  const getAlertBreakdown = () => {
    const byType: Record<string, number> = { CRITICAL: 0, WARNING: 0, CAUTION: 0, INFO: 0 };
    const byCategory: Record<string, number> = {};
    
    alerts.forEach(a => {
      byType[a.type] = (byType[a.type] || 0) + 1;
      byCategory[a.category] = (byCategory[a.category] || 0) + 1;
    });

    return {
      byType,
      byCategory: Object.entries(byCategory).sort((a, b) => b[1] - a[1]).slice(0, 5),
    };
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((card) => (
          <button
            key={card.id}
            onClick={() => handleCardClick(card.id)}
            className={`card p-4 text-left transition-all duration-200 transform 
                      hover:scale-[1.02] hover:-translate-y-1 hover:shadow-lg ${card.glowColor}
                      ${activeCard === card.id ? 'ring-2 ring-offset-2 ring-offset-slate-900 ring-blue-500' : ''}
                      cursor-pointer`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">{card.title}</p>
                <p className="text-2xl font-bold text-white mt-1">{card.value}</p>
              </div>
              <div className={`w-12 h-12 rounded-xl ${card.bgColor} flex items-center justify-center 
                            transition-transform duration-200 ${activeCard === card.id ? 'scale-110' : ''}`}>
                <card.icon className="w-6 h-6" style={{ color: card.iconColor }} />
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Expanded Detail Panel */}
      {activeCard && (
        <div className="card p-4 animate-in slide-in-from-top-2 duration-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-200">
              {activeCard === 'services' && 'Services Breakdown'}
              {activeCard === 'revenue' && 'Revenue Breakdown'}
              {activeCard === 'staff' && 'Staff Overview'}
              {activeCard === 'alerts' && 'Alerts Breakdown'}
            </h3>
            <button
              onClick={() => setActiveCard(null)}
              className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {activeCard === 'services' && (
            <ServiceBreakdown data={getServiceBreakdown()} services={services} />
          )}
          {activeCard === 'revenue' && (
            <RevenueBreakdown data={getRevenueBreakdown()} financials={financials} services={services} />
          )}
          {activeCard === 'staff' && (
            <StaffBreakdown staffMetrics={staffMetrics} />
          )}
          {activeCard === 'alerts' && (
            <AlertBreakdown data={getAlertBreakdown()} alerts={alerts} />
          )}
        </div>
      )}
    </div>
  );
}

interface ServiceBreakdownData {
  byDay: [string, number][];
  byStaff: [string, number][];
  byClient: [string, number][];
  totalHours: number;
}

function ServiceBreakdown({ data, services }: { data: ServiceBreakdownData; services: LTSSService[] }) {
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  // Helper to get day name from date
  const getDayName = (date: Date): string => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[date.getDay()];
  };

  // Get drill-down data for selected day
  const getDayDrillDown = (dayName: string) => {
    // Filter services for this day
    const dayServices = services.filter(s => {
      if (dayName === 'No Date') {
        return !s.dayOfService && !s.serviceDate;
      }
      let day = s.dayOfService;
      if (!day && s.serviceDate) {
        day = getDayName(new Date(s.serviceDate));
      }
      return day === dayName;
    });

    // Group by specific date
    const byDate: Record<string, number> = {};
    dayServices.forEach(s => {
      if (s.serviceDate) {
        const d = new Date(s.serviceDate);
        const dateKey = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
        byDate[dateKey] = (byDate[dateKey] || 0) + 1;
      } else {
        byDate['No Date'] = (byDate['No Date'] || 0) + 1;
      }
    });

    // Group by staff
    const byStaff: Record<string, number> = {};
    dayServices.forEach(s => {
      if (s.staffName) {
        byStaff[s.staffName] = (byStaff[s.staffName] || 0) + 1;
      }
    });

    // Group by client
    const byClient: Record<string, number> = {};
    dayServices.forEach(s => {
      if (s.clientName) {
        byClient[s.clientName] = (byClient[s.clientName] || 0) + 1;
      }
    });

    // Group by service status (helpful for "No Date" investigation)
    const byStatus: Record<string, number> = {};
    dayServices.forEach(s => {
      const status = s.serviceStatus || 'Unknown';
      byStatus[status] = (byStatus[status] || 0) + 1;
    });

    return {
      total: dayServices.length,
      byDate: Object.entries(byDate).sort((a, b) => b[1] - a[1]),
      byStaff: Object.entries(byStaff).sort((a, b) => b[1] - a[1]).slice(0, 8),
      byClient: Object.entries(byClient).sort((a, b) => b[1] - a[1]).slice(0, 8),
      byStatus: Object.entries(byStatus).sort((a, b) => b[1] - a[1]),
      totalHours: dayServices.reduce((sum, s) => sum + (s.serviceLength || 0), 0),
    };
  };

  // If a day is selected, show drill-down view
  if (selectedDay) {
    const drillDown = getDayDrillDown(selectedDay);
    
    return (
      <div className="space-y-4">
        {/* Back button and header */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSelectedDay(null)}
            className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          <h4 className="text-sm font-medium text-slate-300">
            {selectedDay} Services
          </h4>
          <span className="text-xs text-slate-500">
            ({drillDown.total} services, {drillDown.totalHours.toFixed(1)} hours)
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* By Specific Date */}
          <div className="bg-slate-800/50 rounded-lg p-4 max-h-64 overflow-y-auto">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700">
              <Calendar className="w-4 h-4 text-blue-400" />
              <h4 className="text-sm font-medium text-slate-300">By Date</h4>
            </div>
            <div className="space-y-1">
              {drillDown.byDate.map(([date, count]) => (
                <div key={date} className="flex items-center justify-between text-sm py-1">
                  <span className="text-slate-400 text-xs">{date}</span>
                  <span className="text-slate-200 font-medium bg-slate-700/50 px-2 py-0.5 rounded text-xs">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* By Staff */}
          <div className="bg-slate-800/50 rounded-lg p-4 max-h-64 overflow-y-auto">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700">
              <Users className="w-4 h-4 text-purple-400" />
              <h4 className="text-sm font-medium text-slate-300">By Staff</h4>
            </div>
            <div className="space-y-1">
              {drillDown.byStaff.map(([name, count]) => (
                <div key={name} className="flex items-center justify-between text-sm py-1">
                  <span className="text-slate-400 truncate mr-2 text-xs">{name}</span>
                  <span className="text-purple-400 font-medium bg-purple-500/10 px-2 py-0.5 rounded text-xs">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* By Client + Status */}
          <div className="space-y-4">
            <div className="bg-slate-800/50 rounded-lg p-4 max-h-32 overflow-y-auto">
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700">
                <Users className="w-4 h-4 text-green-400" />
                <h4 className="text-sm font-medium text-slate-300">By Client</h4>
              </div>
              <div className="space-y-1">
                {drillDown.byClient.slice(0, 4).map(([name, count]) => (
                  <div key={name} className="flex items-center justify-between text-sm py-1">
                    <span className="text-slate-400 truncate mr-2 text-xs">{name}</span>
                    <span className="text-green-400 font-medium bg-green-500/10 px-2 py-0.5 rounded text-xs">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Service Status breakdown - helpful for investigating "No Date" */}
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700">
                <AlertTriangle className="w-4 h-4 text-yellow-400" />
                <h4 className="text-sm font-medium text-slate-300">By Status</h4>
              </div>
              <div className="space-y-1">
                {drillDown.byStatus.map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between text-sm py-1">
                    <span className={`text-xs truncate mr-2 ${
                      status.includes('Missing') ? 'text-red-400' : 
                      status === 'Closed' ? 'text-green-400' : 'text-slate-400'
                    }`}>{status}</span>
                    <span className="text-slate-200 font-medium bg-slate-700/50 px-2 py-0.5 rounded text-xs">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* By Day - Clickable */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700">
          <Calendar className="w-4 h-4 text-blue-400" />
          <h4 className="text-sm font-medium text-slate-300">By Day</h4>
          <span className="text-xs text-slate-500">(click to drill down)</span>
        </div>
        <div className="space-y-2">
          {data.byDay.map(([day, count]) => (
            <button
              key={day}
              onClick={() => count > 0 && setSelectedDay(day)}
              disabled={count === 0}
              className={`w-full flex items-center justify-between text-sm py-1 px-2 rounded transition-colors ${
                count > 0 
                  ? 'hover:bg-slate-700/50 cursor-pointer' 
                  : 'opacity-50 cursor-not-allowed'
              }`}
            >
              <span className="text-slate-400">{day}</span>
              <span className={`font-medium px-2 py-0.5 rounded ${
                day === 'No Date' 
                  ? 'text-yellow-400 bg-yellow-500/10' 
                  : 'text-slate-200 bg-slate-700/50'
              }`}>{count}</span>
            </button>
          ))}
        </div>
      </div>
      
      {/* Top Staff */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700">
          <Users className="w-4 h-4 text-purple-400" />
          <h4 className="text-sm font-medium text-slate-300">Top Staff</h4>
        </div>
        <div className="space-y-2">
          {data.byStaff.map(([name, count]) => (
            <div key={name} className="flex items-center justify-between text-sm py-1">
              <span className="text-slate-400 truncate mr-2">{name}</span>
              <span className="text-slate-200 font-medium bg-slate-700/50 px-2 py-0.5 rounded whitespace-nowrap">{count}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Summary */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700">
          <Clock className="w-4 h-4 text-green-400" />
          <h4 className="text-sm font-medium text-slate-300">Summary</h4>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-400">Total Hours</span>
            <span className="text-lg font-semibold text-green-400">{data.totalHours.toFixed(1)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-400">Unique Clients</span>
            <span className="text-lg font-semibold text-blue-400">{data.byClient.length}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

interface RevenueBreakdownProps {
  data: { byWeek: [string, number][]; topEarners: [string, number][]; avgPerService: number };
  financials?: FinancialSummary;
  services?: LTSSService[];
}

function RevenueBreakdown({ data, financials, services = [] }: RevenueBreakdownProps) {
  const [selectedWeek, setSelectedWeek] = useState<string | null>(null);
  const [selectedStaff, setSelectedStaff] = useState<string | null>(null);

  // Get services for a specific week
  const getWeekServices = (week: string) => {
    return services.filter(s => s.week === week && s.amountPaid && s.amountPaid > 0);
  };

  // Get services for a specific staff member
  const getStaffServices = (staffName: string) => {
    return services.filter(s => s.staffName === staffName && s.amountPaid && s.amountPaid > 0);
  };

  // Drill-down view for week
  if (selectedWeek) {
    const weekServices = getWeekServices(selectedWeek);
    const byStaff: Record<string, { count: number; amount: number }> = {};
    const byClient: Record<string, { count: number; amount: number }> = {};
    
    weekServices.forEach(s => {
      if (s.staffName) {
        if (!byStaff[s.staffName]) byStaff[s.staffName] = { count: 0, amount: 0 };
        byStaff[s.staffName].count++;
        byStaff[s.staffName].amount += s.amountPaid || 0;
      }
      if (s.clientName) {
        if (!byClient[s.clientName]) byClient[s.clientName] = { count: 0, amount: 0 };
        byClient[s.clientName].count++;
        byClient[s.clientName].amount += s.amountPaid || 0;
      }
    });

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSelectedWeek(null)}
            className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          <h4 className="text-sm font-medium text-slate-300">
            Week: {selectedWeek}
          </h4>
          <span className="text-xs text-slate-500">
            ({weekServices.length} services, {formatCurrency(weekServices.reduce((sum, s) => sum + (s.amountPaid || 0), 0))})
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* By Staff */}
          <div className="bg-slate-800/50 rounded-lg p-4 max-h-64 overflow-y-auto">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700">
              <Users className="w-4 h-4 text-purple-400" />
              <h4 className="text-sm font-medium text-slate-300">By Staff</h4>
            </div>
            <div className="space-y-1">
              {Object.entries(byStaff)
                .sort((a, b) => b[1].amount - a[1].amount)
                .map(([name, data]) => (
                  <div key={name} className="flex items-center justify-between text-sm py-1">
                    <span className="text-slate-400 truncate mr-2 text-xs">{name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">{data.count} svc</span>
                      <span className="text-green-400 font-medium text-xs">{formatCurrency(data.amount)}</span>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* By Client */}
          <div className="bg-slate-800/50 rounded-lg p-4 max-h-64 overflow-y-auto">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700">
              <Users className="w-4 h-4 text-blue-400" />
              <h4 className="text-sm font-medium text-slate-300">By Client</h4>
            </div>
            <div className="space-y-1">
              {Object.entries(byClient)
                .sort((a, b) => b[1].amount - a[1].amount)
                .map(([name, data]) => (
                  <div key={name} className="flex items-center justify-between text-sm py-1">
                    <span className="text-slate-400 truncate mr-2 text-xs">{name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">{data.count} svc</span>
                      <span className="text-green-400 font-medium text-xs">{formatCurrency(data.amount)}</span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Drill-down view for staff
  if (selectedStaff) {
    const staffServices = getStaffServices(selectedStaff);
    const byWeek: Record<string, { count: number; amount: number }> = {};
    const byClient: Record<string, { count: number; amount: number }> = {};
    
    staffServices.forEach(s => {
      const week = s.week || 'Unknown';
      if (!byWeek[week]) byWeek[week] = { count: 0, amount: 0 };
      byWeek[week].count++;
      byWeek[week].amount += s.amountPaid || 0;
      
      if (s.clientName) {
        if (!byClient[s.clientName]) byClient[s.clientName] = { count: 0, amount: 0 };
        byClient[s.clientName].count++;
        byClient[s.clientName].amount += s.amountPaid || 0;
      }
    });

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSelectedStaff(null)}
            className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          <h4 className="text-sm font-medium text-slate-300">
            Staff: {selectedStaff}
          </h4>
          <span className="text-xs text-slate-500">
            ({staffServices.length} services, {formatCurrency(staffServices.reduce((sum, s) => sum + (s.amountPaid || 0), 0))})
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* By Week */}
          <div className="bg-slate-800/50 rounded-lg p-4 max-h-64 overflow-y-auto">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700">
              <Calendar className="w-4 h-4 text-green-400" />
              <h4 className="text-sm font-medium text-slate-300">By Week</h4>
            </div>
            <div className="space-y-1">
              {Object.entries(byWeek)
                .sort((a, b) => b[1].amount - a[1].amount)
                .map(([week, data]) => (
                  <div key={week} className="flex items-center justify-between text-sm py-1">
                    <span className="text-slate-400 truncate mr-2 text-xs">{week}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">{data.count} svc</span>
                      <span className="text-green-400 font-medium text-xs">{formatCurrency(data.amount)}</span>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* By Client */}
          <div className="bg-slate-800/50 rounded-lg p-4 max-h-64 overflow-y-auto">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700">
              <Users className="w-4 h-4 text-blue-400" />
              <h4 className="text-sm font-medium text-slate-300">By Client</h4>
            </div>
            <div className="space-y-1">
              {Object.entries(byClient)
                .sort((a, b) => b[1].amount - a[1].amount)
                .map(([name, data]) => (
                  <div key={name} className="flex items-center justify-between text-sm py-1">
                    <span className="text-slate-400 truncate mr-2 text-xs">{name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">{data.count} svc</span>
                      <span className="text-emerald-400 font-medium text-xs">{formatCurrency(data.amount)}</span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* By Week */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700">
          <Calendar className="w-4 h-4 text-green-400" />
          <h4 className="text-sm font-medium text-slate-300">By Week</h4>
          <span className="text-xs text-slate-500">(click to drill down)</span>
        </div>
        <div className="space-y-2">
          {data.byWeek.map(([week, amount]) => (
            <button
              key={week}
              onClick={() => setSelectedWeek(week)}
              className="w-full flex items-center justify-between text-sm py-1 px-2 rounded hover:bg-slate-700/50 transition-colors cursor-pointer"
            >
              <span className="text-slate-400 truncate mr-2">{week}</span>
              <span className="text-green-400 font-medium whitespace-nowrap">{formatCurrency(amount)}</span>
            </button>
          ))}
        </div>
      </div>
      
      {/* Top Earners */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700">
          <TrendingUp className="w-4 h-4 text-emerald-400" />
          <h4 className="text-sm font-medium text-slate-300">Top Earners</h4>
          <span className="text-xs text-slate-500">(click to drill down)</span>
        </div>
        <div className="space-y-2">
          {data.topEarners.map(([name, amount]) => (
            <button
              key={name}
              onClick={() => setSelectedStaff(name)}
              className="w-full flex items-center justify-between text-sm py-1 px-2 rounded hover:bg-slate-700/50 transition-colors cursor-pointer"
            >
              <span className="text-slate-400 truncate mr-2">{name}</span>
              <span className="text-emerald-400 font-medium whitespace-nowrap">{formatCurrency(amount)}</span>
            </button>
          ))}
        </div>
      </div>
      
      {/* Summary */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700">
          <DollarSign className="w-4 h-4 text-yellow-400" />
          <h4 className="text-sm font-medium text-slate-300">Summary</h4>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-400">Avg/Service</span>
            <span className="text-lg font-semibold text-yellow-400">{formatCurrency(data.avgPerService)}</span>
          </div>
          {financials && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">Claims Paid</span>
                <span className="text-lg font-semibold text-green-400">{financials.claimsPaid}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">Pending</span>
                <span className="text-lg font-semibold text-yellow-400">{financials.claimsPending}</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Component for displaying correction count with hover tooltip showing dates
function CorrectionBadge({ staff }: { staff: StaffMetrics }) {
  const [showTooltip, setShowTooltip] = useState(false);
  
  // Format date for display
  const formatDate = (date: Date) => {
    const d = new Date(date);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayName = days[d.getDay()];
    return `${dayName}, ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  // Sort dates chronologically
  const sortedDates = staff.manualCorrectionDates 
    ? [...staff.manualCorrectionDates].sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
    : [];

  return (
    <div className="flex items-center justify-between text-sm py-1">
      <span className="text-slate-400 truncate mr-2">{staff.staffName}</span>
      <div 
        className="relative"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <span className={`font-medium px-2 py-0.5 rounded whitespace-nowrap cursor-pointer ${
          staff.penaltyStatus === 'exceeded' 
            ? 'text-red-400 bg-red-500/10' 
            : 'text-yellow-400 bg-yellow-500/10'
        }`}>
          {staff.manualCorrections} corr.
        </span>
        
        {/* Tooltip */}
        {showTooltip && sortedDates.length > 0 && (
          <div className="absolute bottom-full right-0 mb-2 z-50">
            <div className="bg-slate-800 border border-slate-600 rounded-lg shadow-xl p-3 min-w-[200px]">
              <div className="text-xs font-semibold text-slate-300 mb-2 border-b border-slate-600 pb-1">
                Adjustment Dates ({staff.manualCorrections})
              </div>
              <div className="space-y-1 max-h-[150px] overflow-y-auto">
                {sortedDates.map((date, index) => (
                  <div key={index} className="text-xs text-slate-400">
                    {formatDate(date)}
                  </div>
                ))}
              </div>
            </div>
            {/* Arrow */}
            <div className="absolute top-full right-4 border-8 border-transparent border-t-slate-600" />
          </div>
        )}
        
        {/* Show message when no dates available */}
        {showTooltip && staff.manualCorrections > 0 && sortedDates.length === 0 && (
          <div className="absolute bottom-full right-0 mb-2 z-50">
            <div className="bg-slate-800 border border-slate-600 rounded-lg shadow-xl p-3">
              <div className="text-xs text-slate-400">
                No date information available
              </div>
            </div>
            <div className="absolute top-full right-4 border-8 border-transparent border-t-slate-600" />
          </div>
        )}
      </div>
    </div>
  );
}

function StaffBreakdown({ staffMetrics }: { staffMetrics: StaffMetrics[] }) {
  const topPerformers = [...staffMetrics].sort((a, b) => b.totalServices - a.totalServices).slice(0, 5);
  const needsAttention = staffMetrics.filter(s => s.penaltyStatus === 'exceeded' || s.penaltyStatus === 'risk');
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Top Performers */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700">
          <TrendingUp className="w-4 h-4 text-purple-400" />
          <h4 className="text-sm font-medium text-slate-300">Top Performers</h4>
        </div>
        <div className="space-y-2">
          {topPerformers.map(staff => (
            <div key={staff.staffName} className="flex items-center justify-between text-sm py-1">
              <span className="text-slate-400 truncate mr-2">{staff.staffName}</span>
              <span className="text-purple-400 font-medium bg-purple-500/10 px-2 py-0.5 rounded whitespace-nowrap">{staff.totalServices}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Needs Attention */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <h4 className="text-sm font-medium text-slate-300">Needs Attention</h4>
        </div>
        <div className="space-y-2">
          {needsAttention.length === 0 ? (
            <p className="text-sm text-green-400">All staff in good standing</p>
          ) : (
            needsAttention.slice(0, 5).map(staff => (
              <CorrectionBadge key={staff.staffName} staff={staff} />
            ))
          )}
        </div>
      </div>
      
      {/* Summary */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700">
          <Clock className="w-4 h-4 text-blue-400" />
          <h4 className="text-sm font-medium text-slate-300">Summary</h4>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-400">Total Staff</span>
            <span className="text-lg font-semibold text-blue-400">{staffMetrics.length}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-400">At Risk</span>
            <span className={`text-lg font-semibold ${needsAttention.length > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
              {needsAttention.length}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-400">Avg Clean Rate</span>
            <span className="text-lg font-semibold text-green-400">
              {staffMetrics.length > 0 
                ? Math.round(staffMetrics.reduce((sum, s) => sum + s.cleanRate, 0) / staffMetrics.length)
                : 0}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function AlertBreakdown({ data, alerts }: { 
  data: { byType: Record<string, number>; byCategory: [string, number][] };
  alerts: Alert[];
}) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Get staff breakdown for selected category
  const getStaffBreakdown = (category: string): { staffName: string; count: number; alerts: Alert[] }[] => {
    const categoryAlerts = alerts.filter(a => a.category === category);
    const staffMap: Record<string, { count: number; alerts: Alert[] }> = {};
    
    categoryAlerts.forEach(alert => {
      const staff = alert.staffName || alert.clientName || 'Unknown';
      if (!staffMap[staff]) {
        staffMap[staff] = { count: 0, alerts: [] };
      }
      staffMap[staff].count++;
      staffMap[staff].alerts.push(alert);
    });
    
    return Object.entries(staffMap)
      .map(([staffName, data]) => ({ staffName, ...data }))
      .sort((a, b) => b.count - a.count);
  };

  // If a category is selected, show staff breakdown
  if (selectedCategory) {
    const staffBreakdown = getStaffBreakdown(selectedCategory);
    
    return (
      <div className="space-y-4">
        {/* Back button and header */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSelectedCategory(null)}
            className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          <h4 className="text-sm font-medium text-slate-300">
            {selectedCategory} - By Staff/Client
          </h4>
          <span className="text-xs text-slate-500">
            ({alerts.filter(a => a.category === selectedCategory).length} total)
          </span>
        </div>
        
        {/* Staff breakdown list */}
        <div className="bg-slate-800/50 rounded-lg p-4 max-h-80 overflow-y-auto">
          <div className="space-y-3">
            {staffBreakdown.map(({ staffName, count, alerts: staffAlerts }) => (
              <div key={staffName} className="border-b border-slate-700 pb-3 last:border-0 last:pb-0">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-300">{staffName}</span>
                  <span className="text-sm font-semibold text-red-400 bg-red-500/10 px-2 py-0.5 rounded">
                    {count} {count === 1 ? 'alert' : 'alerts'}
                  </span>
                </div>
                {/* Show first few alert details */}
                <div className="space-y-1 pl-2 border-l-2 border-slate-700">
                  {staffAlerts.slice(0, 3).map((alert, idx) => {
                    // Format the date if available
                    const dateStr = alert.timestamp 
                      ? new Date(alert.timestamp).toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric' 
                        })
                      : '';
                    
                    return (
                      <p key={idx} className="text-xs text-slate-500 truncate">
                        {dateStr && <span className="text-slate-400">{dateStr}: </span>}
                        {alert.message}
                      </p>
                    );
                  })}
                  {staffAlerts.length > 3 && (
                    <p className="text-xs text-slate-600">+{staffAlerts.length - 3} more...</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* By Severity */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <h4 className="text-sm font-medium text-slate-300">By Severity</h4>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-1">
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-red-500"></span>
              <span className="text-sm text-slate-400">Critical</span>
            </span>
            <span className="text-lg font-semibold text-red-400">{data.byType.CRITICAL}</span>
          </div>
          <div className="flex items-center justify-between py-1">
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-orange-500"></span>
              <span className="text-sm text-slate-400">Warning</span>
            </span>
            <span className="text-lg font-semibold text-orange-400">{data.byType.WARNING}</span>
          </div>
          <div className="flex items-center justify-between py-1">
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
              <span className="text-sm text-slate-400">Caution</span>
            </span>
            <span className="text-lg font-semibold text-yellow-400">{data.byType.CAUTION}</span>
          </div>
          <div className="flex items-center justify-between py-1">
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-blue-500"></span>
              <span className="text-sm text-slate-400">Info</span>
            </span>
            <span className="text-lg font-semibold text-blue-400">{data.byType.INFO}</span>
          </div>
        </div>
      </div>
      
      {/* By Category - Clickable */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700">
          <FileText className="w-4 h-4 text-purple-400" />
          <h4 className="text-sm font-medium text-slate-300">By Category</h4>
          <span className="text-xs text-slate-500">(click to see by staff)</span>
        </div>
        <div className="space-y-2">
          {data.byCategory.map(([category, count]) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className="w-full flex items-center justify-between text-sm py-1.5 px-2 rounded hover:bg-slate-700/50 transition-colors group"
            >
              <span className="text-slate-400 group-hover:text-slate-300">{category}</span>
              <span className="text-slate-200 font-medium bg-slate-700/50 group-hover:bg-slate-600/50 px-2 py-0.5 rounded cursor-pointer">
                {count}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
